/**
 * ShopGate – canonical gating for Gårdsbutik visibility.
 *
 * Single source of truth used by:
 * - Portal: ProducerShopOverview (banner + publish guard)
 * - Public: ProducerShop page (/producer/:slug/shop)
 * - Producer detail: "Från gårdsbutiken" section
 *
 * Evaluation order (first failing layer wins):
 * 1. needs_public      – producer.status != 'public'
 * 2. paused / banned    – producer_status in ('paused','banned')
 * 3. needs_entitlement  – farm_shop feature not enabled
 * 4. needs_verification – verified = false
 * 5. needs_enable       – shop_enabled = false
 * 6. allowed
 */

export type ShopGateReasonKey =
  | 'needs_public'
  | 'paused'
  | 'banned'
  | 'needs_entitlement'
  | 'needs_verification'
  | 'needs_enable'
  | 'allowed';

export interface ShopGateResult {
  allowed: boolean;
  reason_key: ShopGateReasonKey;
  title_sv: string;
  badge_sv: string;
  message_sv: string;
  /** CTA for portal context */
  cta_label_sv?: string;
}

export interface ShopGateInput {
  /** Producer fields */
  status: string; // 'draft' | 'public' | ...
  producer_status: string; // 'pending' | 'active' | 'paused' | 'banned'
  verified: boolean;
  shop_enabled: boolean;
  /** Whether farm_shop entitlement is enabled */
  hasFarmShopEntitlement: boolean;
}

const GATE_CONFIG: Record<Exclude<ShopGateReasonKey, 'allowed'>, Omit<ShopGateResult, 'allowed' | 'reason_key'>> = {
  needs_public: {
    title_sv: 'Gårdsbutik är inte tillgänglig',
    badge_sv: 'Ej publicerad',
    message_sv: 'Din gård måste vara publicerad innan gårdsbutiken kan visas.',
    cta_label_sv: 'Publicera gården',
  },
  paused: {
    title_sv: 'Gårdsbutik är pausad',
    badge_sv: 'Pausad',
    message_sv: 'Din gård är pausad. Kontakta Farmly för mer information.',
  },
  banned: {
    title_sv: 'Gårdsbutik är avstängd',
    badge_sv: 'Avstängd',
    message_sv: 'Din gård har stängts av. Kontakta Farmly för mer information.',
  },
  needs_entitlement: {
    title_sv: 'Gårdsbutik är inte tillgänglig',
    badge_sv: 'Inte aktiverad',
    message_sv: 'Gårdsbutiken ingår inte i din plan. Kontakta Farmly för mer information.',
  },
  needs_verification: {
    title_sv: 'Gårdsbutik är låst',
    badge_sv: 'Väntar på verifiering',
    message_sv: 'Din gård måste vara verifierad innan du kan använda gårdsbutiken. Du kan skapa produkter som utkast medan du väntar på godkännande.',
  },
  needs_enable: {
    title_sv: 'Gårdsbutiken',
    badge_sv: 'Dold',
    message_sv: 'Gårdsbutiken är inte tillgänglig just nu.',
    cta_label_sv: 'Visa gårdsbutik publikt',
  },
};

export function evaluateShopGate(input: ShopGateInput): ShopGateResult {
  // 1. Producer must be public
  if (input.status !== 'public') {
    return { allowed: false, reason_key: 'needs_public', ...GATE_CONFIG.needs_public };
  }

  // 2. Producer must not be paused/banned
  if (input.producer_status === 'paused') {
    return { allowed: false, reason_key: 'paused', ...GATE_CONFIG.paused };
  }
  if (input.producer_status === 'banned') {
    return { allowed: false, reason_key: 'banned', ...GATE_CONFIG.banned };
  }

  // 3. Must have farm_shop entitlement
  if (!input.hasFarmShopEntitlement) {
    return { allowed: false, reason_key: 'needs_entitlement', ...GATE_CONFIG.needs_entitlement };
  }

  // 4. Must be verified
  if (!input.verified) {
    return { allowed: false, reason_key: 'needs_verification', ...GATE_CONFIG.needs_verification };
  }

  // 5. Must have shop_enabled
  if (!input.shop_enabled) {
    return { allowed: false, reason_key: 'needs_enable', ...GATE_CONFIG.needs_enable };
  }

  // 6. All clear
  return {
    allowed: true,
    reason_key: 'allowed',
    title_sv: 'Gårdsbutik',
    badge_sv: 'Aktiv',
    message_sv: '',
  };
}

/**
 * For preview mode: shop section should be visible even when
 * reason is needs_enable or needs_verification (so the producer can preview).
 */
export function isVisibleInPreview(reason_key: ShopGateReasonKey): boolean {
  return reason_key === 'allowed' || reason_key === 'needs_enable' || reason_key === 'needs_verification';
}
